import React from 'react';
import classnames from 'classnames/bind';
import { TextInput } from '@mantine/core';
import { useDebouncedValue } from '@tanstack/react-pacer';

import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useHasAccess } from 'hooks/useHasAccess';

import { CreateTagDialog } from 'tags/components/CreateTagDialog/CreateTagDialog';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { ScrollArea } from 'components/ScrollArea/ScrollArea';
import { Separator } from 'components/Separator/Separator';
import { TagsTree } from 'tags/components/TagsTree/TagsTree';

import styles from './Sidebar.module.scss';

const c = classnames.bind(styles);

export type SidebarProps = React.PropsWithChildren<{
  footer?: React.ReactNode;
  hasTagsTree?: boolean;
}>;

export function Sidebar({ children, footer, hasTagsTree }: SidebarProps) {
  const [hasAccess] = useHasAccess();

  const { data: currentPlan, isSuccess } = useCurrentPlan();
  const { data: organization } = useCurrentOrganization();

  const canCreateTags =
    (isSuccess && currentPlan === null) ||
    Boolean(currentPlan?.can_create_tags);

  const shouldDisplayTags = isSuccess && organization && hasTagsTree;

  const [tagsSearch, setTagsSearch] = React.useState('');
  const [debouncedTagsSearch] = useDebouncedValue(tagsSearch, { wait: 250 });

  return (
    <div className={c('nav-wrap')}>
      <ScrollArea className={c('nav-scroll')}>
        <nav className={c('nav')}>
          <ul className={c('nav-list')} id="up-sidebar-navigation">
            {children}
          </ul>

          {shouldDisplayTags && (
            <>
              <Separator />

              {canCreateTags && (
                <>
                  <div className={c('labels-header')}>
                    <Heading level="4">Labels</Heading>
                    {canCreateTags && hasTagsTree && hasAccess('deploy') && (
                      <CreateTagDialog />
                    )}
                  </div>

                  <label className={c('search')}>
                    <TextInput
                      aria-label="Search labels"
                      className={c('search-input')}
                      mb="sm"
                      variant="search"
                      leftSection={<Icon name="search" />}
                      type="search"
                      placeholder="Search..."
                      size="sm"
                      value={tagsSearch}
                      onChange={(event) => setTagsSearch(event.target.value)}
                    />
                  </label>

                  <TagsTree searchTerm={debouncedTagsSearch} />
                </>
              )}
            </>
          )}

        </nav>
      </ScrollArea>

      {footer && <div className={c('footer')}>{footer}</div>}
    </div>
  );
}
